import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Package, Store, Image, TrendingUp } from 'lucide-react';
import api from '../../lib/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalPartners: 0,
    newsletterSubscribers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [productsCountRes, partnersRes, newsletterRes] = await Promise.all([
        api.get('/products/count'),
        api.get('/partners'),
        api.get('/newsletter').catch(() => ({ data: [] })),
      ]);

      setStats({
        totalUsers: 0, // Will need a dedicated endpoint
        totalProducts: productsCountRes.data.count,
        totalPartners: partnersRes.data.length,
        newsletterSubscribers: newsletterRes.data.length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Produits',
      value: stats.totalProducts,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Partenaires',
      value: stats.totalPartners,
      icon: Store,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Newsletter',
      value: stats.newsletterSubscribers,
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="admin-dashboard-title">
            Tableau de Bord Admin
          </h1>
          <p className="text-muted-foreground mt-2">
            Vue d'ensemble de votre plateforme Games Visor
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} data-testid={`stat-card-${index}`}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </CardTitle>
                      <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                        <Icon className={`h-5 w-5 ${stat.color}`} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stat.value}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions Rapides</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <a
                    href="/dashboard/admin/produits"
                    className="flex flex-col items-center justify-center p-6 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-center"
                    data-testid="quick-action-products"
                  >
                    <Package className="h-8 w-8 mb-2" />
                    <span className="font-medium">Gérer les Produits</span>
                  </a>
                  <a
                    href="/dashboard/admin/partenaires"
                    className="flex flex-col items-center justify-center p-6 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-center"
                    data-testid="quick-action-partners"
                  >
                    <Store className="h-8 w-8 mb-2" />
                    <span className="font-medium">Gérer les Partenaires</span>
                  </a>
                  <a
                    href="/dashboard/admin/pages"
                    className="flex flex-col items-center justify-center p-6 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-center"
                    data-testid="quick-action-pages"
                  >
                    <Image className="h-8 w-8 mb-2" />
                    <span className="font-medium">Gestion des Pages</span>
                  </a>
                  <a
                    href="/dashboard/admin/utilisateurs"
                    className="flex flex-col items-center justify-center p-6 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-center"
                    data-testid="quick-action-users"
                  >
                    <Users className="h-8 w-8 mb-2" />
                    <span className="font-medium">Gérer les Utilisateurs</span>
                  </a>
                  <a
                    href="/dashboard/admin/referentiels"
                    className="flex flex-col items-center justify-center p-6 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-center"
                    data-testid="quick-action-referentiels"
                  >
                    <Package className="h-8 w-8 mb-2" />
                    <span className="font-medium">Référentiels</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
